if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            // Setup variant tracking for apps after DOM is inserted
            this.setupVariantTrackingForApps();

            // Ensure wishlist block positioning works correctly in modal
            // Original position: wishlist block is before title block (as in PDP)
            // CSS position: absolute makes it appear on top right of title
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
              const modalBody = this.modalContent.querySelector('.modal-dialog__body') || this.modalContent;
              const productInfoContainer = modalBody.querySelector('#ProductInfo-quickadd-' + productElement.dataset.originalSection) || 
                                            modalBody.querySelector('[id^="ProductInfo-"]') ||
                                            modalBody.querySelector('.product__info-container') ||
                                            modalBody.querySelector('.product-info-wrapper') || 
                                            modalBody.querySelector('.product__info-wrapper');
              
              if (productInfoContainer) {
                // Find existing wishlist block in modal (already inserted from PDP at original position)
                const existingWishlistBlock = this.findWishlistBlockInElement(productInfoContainer);
                
                if (existingWishlistBlock) {
                  // Find product__title block (the div containing the title)
                  const titleBlock = productInfoContainer.querySelector('.product__title');
                  
                  if (titleBlock) {
                    // Ensure title block has relative positioning for absolute wishlist button
                    // This allows CSS position: absolute (top: 0, right: 48px) to work correctly
                    // The wishlist block stays in its original position (before title), but CSS makes it appear on top right
                    titleBlock.style.position = 'relative';
                  }
                }
              }
            });

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      extractWishlistBlock(responseHTML, productElement) {
        // Try multiple methods to find wishlist block
        // Method 1: Find by class name
        let wishlistBlock = productElement?.querySelector('.product-wishlist-btn')?.closest('[data-block-type*="growave"], [data-block-type*="wishlist"], [id*="growave"], [id*="wishlist"]');
        
        // Method 2: Find by Growave specific selectors
        if (!wishlistBlock) {
          wishlistBlock = productElement?.querySelector('[class*="gw-wl"], [class*="growave"], [data-gw-wishlist]')?.closest('div');
        }

        // Method 3: Find app block container (Shopify app blocks are usually in a div with shopify_attributes)
        if (!wishlistBlock) {
          const appBlocks = productElement?.querySelectorAll('[data-shopify-block-type*="app"], [data-shopify-block-type*="growave"]');
          if (appBlocks && appBlocks.length > 0) {
            // Find the first app block that contains wishlist-related content
            for (const block of appBlocks) {
              if (block.textContent.includes('wishlist') || 
                  block.querySelector('.product-wishlist-btn') || 
                  block.querySelector('[class*="gw-wl"]')) {
                wishlistBlock = block;
                break;
              }
            }
          }
        }

        // Method 4: Find by searching in product-info-wrapper
        if (!wishlistBlock) {
          const productInfoWrapper = productElement?.querySelector('.product-info-wrapper, .product__info-wrapper');
          if (productInfoWrapper) {
            // Look for the first element that might be the wishlist block (usually before title)
            const firstChild = productInfoWrapper.firstElementChild;
            if (firstChild && (
              firstChild.querySelector('.product-wishlist-btn') ||
              firstChild.querySelector('[class*="gw-wl"]') ||
              firstChild.getAttribute('data-shopify-block-type')?.includes('app')
            )) {
              wishlistBlock = firstChild;
            }
          }
        }

        return wishlistBlock;
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        // Keep data-update-url as true so apps can detect variant changes like in PDP
        // This allows Growave and other apps to track variant changes
        productElement.setAttribute('data-update-url', 'true');
      }

      setupVariantTrackingForApps() {
        // Wait for product-info to be connected to DOM
        requestAnimationFrame(() => {
          const productInfoElement = this.modalContent.querySelector('product-info');
          if (productInfoElement && typeof productInfoElement.updateURL === 'function') {
            // Store original updateURL method
            const originalUpdateURL = productInfoElement.updateURL.bind(productInfoElement);
            
            // Override updateURL to prevent browser history updates in modal
            // but keep variant information accessible in DOM for apps (like PDP)
            productInfoElement.updateURL = function(url, variantId) {
              // Update share button URL (needed for share functionality)
              this.querySelector('share-button')?.updateUrl(
                `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
              );
              
              // Don't update browser history in modal (prevents page navigation)
              // Variant information is still available for apps through:
              // 1. variant-selects [data-selected-variant] element (updated by product-info)
              // 2. variant input fields (updated by updateVariantInputs)
              // 3. PUB_SUB_EVENTS.variantChange event (published by product-info)
              
              // Apps like Growave can detect variant changes the same way as in PDP:
              // - DOM observation (variant-selects updates)
              // - Event listeners (variantChange event subscription)
              // - Reading variant data from variant-selects [data-selected-variant]
            };
          }
        });
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Don't remove wishlist block - we'll just reposition it in the modal
        // This preserves event listeners and app state
      }

      findWishlistBlockInElement(element) {
        // Find wishlist block using same methods as extractWishlistBlock
        let wishlistBlock = element?.querySelector('.product-wishlist-btn')?.closest('[data-block-type*="growave"], [data-block-type*="wishlist"], [id*="growave"], [id*="wishlist"]');
        
        if (!wishlistBlock) {
          wishlistBlock = element?.querySelector('[class*="gw-wl"], [class*="growave"], [data-gw-wishlist]')?.closest('div');
        }

        if (!wishlistBlock) {
          const appBlocks = element?.querySelectorAll('[data-shopify-block-type*="app"], [data-shopify-block-type*="growave"]');
          if (appBlocks && appBlocks.length > 0) {
            for (const block of appBlocks) {
              if (block.textContent.includes('wishlist') || 
                  block.querySelector('.product-wishlist-btn') || 
                  block.querySelector('[class*="gw-wl"]')) {
                wishlistBlock = block;
                break;
              }
            }
          }
        }

        if (!wishlistBlock) {
          const productInfoWrapper = element?.querySelector('.product-info-wrapper, .product__info-wrapper');
          if (productInfoWrapper) {
            const firstChild = productInfoWrapper.firstElementChild;
            if (firstChild && (
              firstChild.querySelector('.product-wishlist-btn') ||
              firstChild.querySelector('[class*="gw-wl"]') ||
              firstChild.getAttribute('data-shopify-block-type')?.includes('app')
            )) {
              wishlistBlock = firstChild;
            }
          }
        }

        return wishlistBlock;
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
